import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { server } from "../../server";
import ProfileFeatureWidgetActionMenu from "../Profile/ProfileFeatureWidgetActionMenu";
import ProfileImageGalleryModal from "./ProfileImageGalleryModal";

const HIDDEN_PROFILE_IMAGE_ORIGINS = new Set([
  "ad",
  "custom-ad",
  "custom_ad",
  "ad-format",
  "ad_format",
  "ad-campaign",
  "ad_campaign",
  "advertisement",
  "sponsored",
  "sponsored-post",
  "sponsored_post",
  "product",
  "event",
  "event-avatar",
  "barcode",
  "nutrition",
  "roombanner",
  "roomavatar",
  "brand",
  "brand_catalog_item",
  "message",
  "site-logo",
]);

const getNormalizedImageUrl = (image = {}) => String(image.url || "").split("?")[0].trim().toLowerCase();

const getImageOrigin = (image = {}) => String(image.origin || (image.postId ? "post" : "other")).toLowerCase();

const isGalleryEligibleImage = (image = {}) => Boolean(image.url) && !HIDDEN_PROFILE_IMAGE_ORIGINS.has(getImageOrigin(image));

const dedupeImagesByUrl = (imageList = []) => {
  const seen = new Set();

  return imageList.filter((image, index) => {
    const key = getNormalizedImageUrl(image) || String(image._id || image.key || index);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const MyShopImages = ({ shopId, canAddPhotos = false, emptyActionHref = '#profile-post-composer', ownerName = "Shop", ownerAvatar }) => {
  const [images, setImages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchImages = async () => {
      if (!shopId) {
        console.error("shopId is undefined");
        return;
      }
      try {
        const response = await axios.get(`${server}/image/shop/${shopId}/images`);
        if (response.data.success) {
          setImages(Array.isArray(response.data.images) ? response.data.images : []);
        } else {
          console.error("Failed to fetch shop images");
          setImages([]);
        }
      } catch (error) {
        console.error("Error fetching shop images:", error);
        setImages([]);
      }
    };
  
    fetchImages();
  }, [shopId]);
  

  const openModal = (index = 0) => {
    if (galleryImages.length === 0) {
      return;
    }

    setCurrentImageIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const previousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const handleDeleteImage = (deletedImageId) => {
    setImages((currentImages) => currentImages.filter((image) => String(image.imageId || image._id || image.id) !== String(deletedImageId)));
    setShowModal(false);
    setCurrentImageIndex(0);
  };

  const galleryImages = dedupeImagesByUrl(images.filter(isGalleryEligibleImage));
  const hasImages = galleryImages.length > 0;
  const photoActions = [
    {
      label: 'Add Photos',
      href: emptyActionHref,
      hidden: !canAddPhotos,
    },
    {
      label: 'See All',
      onClick: () => openModal(),
      hidden: !hasImages,
    },
    {
      label: 'Edit Photos',
      disabled: true,
      hidden: !canAddPhotos,
    },
    {
      label: 'Delete Photos',
      disabled: true,
      hidden: !canAddPhotos,
    },
  ];
  const hasPhotoActions = photoActions.some((action) => !action.hidden);

  return (
    <div className="my-images-component">
      <div className="my-images-header">
        <h3>Photos</h3>
        <div className="my-images-header-actions">
          {hasPhotoActions && (
            <ProfileFeatureWidgetActionMenu label="Shop photo actions" actions={photoActions} />
          )}
        </div>
      </div>

      <div className="my-images-component-image-grid">
        {galleryImages.slice(0, 4).map((image, index) => (
          <img
            key={image._id}
            src={image.url}
            alt={`Shop uploaded ${index + 1}`}
            onClick={() => openModal(index)}
            className="my-images-component-thumbnail"
          />
        ))}
      </div>

      {showModal && hasImages && typeof document !== "undefined" ? createPortal(
        <ProfileImageGalleryModal
          images={galleryImages}
          currentImageIndex={Math.min(currentImageIndex, galleryImages.length - 1)}
          onClose={closeModal}
          onPrevious={previousImage}
          onNext={nextImage}
          onGoToImage={goToImage}
          ownerName={ownerName}
          ownerAvatar={ownerAvatar}
          profileType="Shop"
          onDeleteImage={handleDeleteImage}
        />,
        document.body
      ) : null}
    </div>
  );
};

export default MyShopImages;

